// Auth service: Firebase Google sign-in with a domain allowlist, plus ID-token
// retrieval for the studio REST/WS calls. When Firebase is not configured
// (local backend with STUDIO_AUTH_DISABLED), these degrade to a signed-out no-op.
//
// The course catalog lives in a second Firebase project (aura-courses) whose
// Auth sessions are separate. After a successful Studio sign-in the same Google
// credential is replayed into the courses app so one popup opens both
// sessions; a failure there never breaks Studio sign-in (the Courses pages
// offer a retry instead, see services/courses/auth.ts).

import {
  GoogleAuthProvider,
  signInWithCredential,
  signInWithPopup,
  signOut as fbSignOut,
  onAuthStateChanged,
  type User,
  type UserCredential,
} from "firebase/auth";
import { getCoursesAuth, getFirebaseAuth } from "../config/firebase";

export const ALLOWED_DOMAINS = ["wisehomie.com", "talkwithaura.com"];

export function isAllowedDomain(email: string | null): boolean {
  if (!email) return false;
  const domain = email.split("@")[1]?.toLowerCase();
  return !!domain && ALLOWED_DOMAINS.includes(domain);
}

export const ACCESS_DENIED_MESSAGE =
  "Access denied — only @wisehomie.com and @talkwithaura.com accounts are allowed.";

// Studio's own Google popup, on Studio's auth (same-origin authDomain, see
// .env.prod), with the domain allowlist applied. Returns the raw result so
// the Google credential can be replayed into the courses app.
export async function signInWithGooglePopup(): Promise<UserCredential> {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error("Firebase auth is not configured for this deployment.");
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  if (!isAllowedDomain(result.user.email)) {
    await fbSignOut(auth);
    throw new Error(ACCESS_DENIED_MESSAGE);
  }
  return result;
}

export async function signInWithGoogle(): Promise<User> {
  const result = await signInWithGooglePopup();
  try {
    await replayCredentialIntoCourses(result);
  } catch (e) {
    console.warn("Courses sign-in from the Studio credential failed; use the Courses page sign-in.", e);
  }
  return result.user;
}

// Reuse the Google credential from Studio's popup for the courses project.
// Needs the Studio web client id whitelisted in aura-courses (README, "Courses").
// No popup is ever opened on the courses app: its authDomain is cross-origin
// from the Studio site and a cross-origin popup relay fails silently.
export async function replayCredentialIntoCourses(result: UserCredential): Promise<void> {
  const coursesAuth = getCoursesAuth();
  if (!coursesAuth) return;
  const credential = GoogleAuthProvider.credentialFromResult(result);
  if (!credential) throw new Error("Google returned no credential to reuse for the courses project.");
  await signInWithCredential(coursesAuth, credential);
}

// Signs out of both projects. The courses app is signed out even when Studio
// has no Firebase config (local dev with a courses config).
export async function signOut(): Promise<void> {
  const auth = getFirebaseAuth();
  if (auth) await fbSignOut(auth);
  const coursesAuth = getCoursesAuth();
  if (coursesAuth) await fbSignOut(coursesAuth);
}

export function onAuthChange(cb: (user: User | null) => void): () => void {
  const auth = getFirebaseAuth();
  if (!auth) {
    cb(null);
    return () => {};
  }
  return onAuthStateChanged(auth, cb);
}

// Fresh ID token for API calls, or null when signed out / Firebase disabled.
export async function getIdToken(): Promise<string | null> {
  const auth = getFirebaseAuth();
  const user = auth?.currentUser;
  if (!user) return null;
  return user.getIdToken();
}
